import { api, formatApiError, fmtDate, inr } from "@/lib/api";
import { toast } from "sonner";

const MODE_LABELS = { cash: "Cash", upi: "UPI", rtgs_neft: "NEFT / RTGS", cheque: "Cheque", bank: "Bank", other: "Other" };

export const monthLabel = (m) => {
  if (!m || m.length !== 7) return m || "—";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" });
};

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const amt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const PAY_LABELS = { bank: "Bank transfer", rtgs_neft: "Bank transfer", cash: "Cash", cheque: "Cheque", upi: "UPI", other: "Other" };

export const loadLogo = async (url) => {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const b = await (await fetch(url)).blob();
    return await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(b); });
  } catch { return null; }
};

export const buildPayslipPdf = async (data, jsPDF) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const e = data.employee || {};
  const co = data.company || {};
  const s = e.salary_structure || { basic: data.monthly_salary, allowances: [], deductions: [] };
  const monthly = Number(data.monthly_salary) || 0;
  const ratio = monthly > 0 ? (Number(data.earned_salary) || 0) / monthly : 0;
  const gross = [["Basic", s.basic], ...(s.allowances || []).map((a) => [a.name, a.amount])];
  const grossTotal = gross.reduce((t, [, v]) => t + (Number(v) || 0), 0);
  const earned = gross.map(([k, v]) => [k, Math.round((Number(v) || 0) * ratio * 100) / 100]);
  const ded = (s.deductions || []).map((d) => [d.name, Math.round((Number(d.amount) || 0) * ratio * 100) / 100]);
  if (Number(data.advance_given) > 0) ded.push(["Salary Advance", Number(data.advance_given)]);
  const earnedTotal = earned.reduce((t, [, v]) => t + v, 0);
  const dedTotal = ded.reduce((t, [, v]) => t + v, 0);

  const L = 12, R = 198, M = 95, D = 150;
  doc.setLineWidth(0.4); doc.setDrawColor(0);
  const logo = await loadLogo(co.logo_url || "/brand/vetrich-logo-pdf.jpg");
  if (logo) { try { doc.addImage(logo, /^data:image\/jpe?g/.test(logo) ? "JPEG" : "PNG", 16, 15, 26, 22, undefined, "FAST"); } catch { /* unsupported image */ } }
  doc.setFont("helvetica", "bold"); doc.setFontSize(18);
  doc.text((co.name || "").toUpperCase(), 120, 26, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  if (co.address) doc.text(doc.splitTextToSize(co.address, 120), 120, 32, { align: "center" });
  doc.line(L, 42, R, 42);
  doc.setFont("helvetica", "bold");
  doc.text(`Salary Slip for the month of      ${new Date(`${data.month}-01`).toLocaleString("en-GB", { month: "long", year: "numeric" })}`, 105, 47.5, { align: "center" });
  doc.line(L, 50, R, 50);

  doc.setFont("helvetica", "normal");
  [["Name Of Employee", e.name || data.employee_name], ["Emp Id", e.employee_code || "—"], ["Designation", e.designation || "—"], ["Present days", String(data.employee_working_days ?? 0)]]
    .forEach(([k, v], i) => { doc.text(k, 17, 56 + i * 5); doc.text(String(v), 82, 56 + i * 5); });
  doc.line(L, 76, R, 76);

  doc.setFont("helvetica", "bolditalic"); doc.setFontSize(11);
  doc.text("Gross Salary", (L + M) / 2, 81.5, { align: "center" });
  doc.text("Net Salary", (M + R) / 2, 81.5, { align: "center" });
  doc.line(L, 84, R, 84);
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("Total Salary", (M + D) / 2, 89, { align: "center" });
  doc.text("Less :- Deductions", (D + R) / 2, 89, { align: "center" });
  doc.line(M, 91, R, 91);
  doc.text("Rs.", M - 2, 89, { align: "right" });
  doc.text("Rs.", D - 2, 96, { align: "right" }); doc.text("Rs.", R - 2, 96, { align: "right" });

  const step = 5.5, n = Math.max(gross.length, ded.length, 3);
  doc.setFont("helvetica", "normal");
  gross.forEach(([k, v], i) => { doc.text(String(k), L + 1, 95 + i * step); doc.text(amt(v), M - 2, 95 + i * step, { align: "right" }); });
  doc.setFont("helvetica", "bold");
  earned.forEach(([k, v], i) => { doc.text(String(k), M + 2, 102 + i * step); doc.text(amt(v), D - 2, 102 + i * step, { align: "right" }); });
  ded.forEach(([k, v], i) => { doc.text(String(k), D + 2, 102 + i * step); doc.text(amt(v), R - 2, 102 + i * step, { align: "right" }); });

  const yt = 104 + n * step;
  doc.setLineWidth(0.2); doc.line(D - 30, yt - 4.5, D - 1, yt - 4.5); doc.line(R - 30, yt - 3.5, R - 1, yt - 3.5); doc.setLineWidth(0.4);
  doc.text("Gross Total", L + 1, yt + 1); doc.text(amt(grossTotal), M - 2, yt + 1, { align: "right" });
  doc.text(amt(earnedTotal), D - 2, yt - 0.5, { align: "right" }); doc.text(amt(dedTotal), R - 2, yt + 1.5, { align: "right" });
  doc.line(M, yt + 3, R, yt + 3);
  doc.setFontSize(15);
  doc.text("Net Payable", M + 3, yt + 10.5); doc.text(amt(data.final_payable), R - 2, yt + 10.5, { align: "right" });
  doc.setFontSize(10);
  const yf = yt + 14;
  doc.line(L, yf, R, yf);
  doc.text("Mode of Payment", (L + M) / 2, yf + 9, { align: "center" });
  doc.text(PAY_LABELS[data.payment_mode] || PAY_LABELS[e.payment_method] || "—", (L + M) / 2, yf + 25, { align: "center" });
  doc.text("Accounts Dept.", (M + R) / 2, yf + 9, { align: "center" });
  doc.setFont("helvetica", "italic"); doc.setFontSize(9);
  doc.text("This is a system generated copy.", (M + R) / 2, yf + 19, { align: "center" });
  doc.text("Signature not required.", (M + R) / 2, yf + 24, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  const yb = yf + 30;
  doc.line(M, 76, M, yb); doc.line(D, 84, D, yt + 3);
  doc.rect(L, 12, R - L, yb - 12);
  doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(110);
  doc.text("Need not require signature — this is a system generated copy.", 105, yb + 6, { align: "center" });
  doc.setTextColor(0);
  return doc;
};

export const downloadPayslipPdf = async (recId) => {
  try {
    const [{ data }, { jsPDF }] = await Promise.all([api.get(`/salary/records/${recId}/payslip`), import("jspdf")]);
    const doc = await buildPayslipPdf(data, jsPDF);
    doc.save(`Payslip_${(data.employee?.name || data.employee_name || "employee").replace(/\W+/g, "_")}_${data.month}.pdf`);
  } catch (err) { toast.error(formatApiError(err)); }
};

export const printPayslip = async (recId) => {
  try {
    const [{ data }, { jsPDF }] = await Promise.all([api.get(`/salary/records/${recId}/payslip`), import("jspdf")]);
    const doc = await buildPayslipPdf(data, jsPDF);
    doc.autoPrint();
    const w = window.open(doc.output("bloburl"), "_blank");
    if (!w) toast.error("Popup blocked — allow popups to print the payslip");
  } catch (err) { toast.error(formatApiError(err)); }
};

export const printPayslipHtml = async (recId) => {
  try {
    const { data } = await api.get(`/salary/records/${recId}/payslip`);
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — allow popups to print the payslip"); return; }
    const adv = (data.advances || []).map((a) => `<tr><td>${fmtDate(a.date)}</td><td class="num mono">${inr(a.amount)}</td><td>${esc(a.notes || "")}</td></tr>`).join("") || `<tr><td colspan="3" class="muted">— no advances —</td></tr>`;
    w.document.write(`
<!doctype html><html><head><title>Payslip · ${esc(data.employee_name)} · ${monthLabel(data.month)}</title>
<style>
  body{font-family:'IBM Plex Sans',Arial,sans-serif;max-width:720px;margin:24px auto;color:#222;padding:0 16px}
  h1{font-size:22px;margin:0 0 4px}.sub{color:#666;font-size:13px;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:18px}
  th,td{padding:8px 10px;border-bottom:1px solid #eee;text-align:left}
  th{text-transform:uppercase;letter-spacing:0.06em;font-size:11px;color:#666;background:#fafafa}
  .num{text-align:right}.mono{font-family:monospace}
  .total td{background:#f3f3f3;font-weight:bold}
  .muted{color:#999;text-align:center;padding:14px}
  .pill{display:inline-block;padding:3px 10px;border-radius:12px;font-size:11px;letter-spacing:0.06em;text-transform:uppercase}
  .paid{background:#dcfce7;color:#166534}.pending{background:#fef3c7;color:#92400e}
</style></head><body>
<h1>Payslip — ${esc(data.employee.name)}</h1>
<div class="sub">${data.employee.employee_code ? `ID: <span class="mono">${esc(data.employee.employee_code)}</span> · ` : ""}${esc(data.employee.designation || "")} · ${esc(data.employee.phone || "—")} · ${monthLabel(data.month)}
<span class="pill ${data.payment_status === "paid" ? "paid" : "pending"}">${esc(data.payment_status).toUpperCase()}</span>
${data.voucher_number ? ` · Voucher: <span class="mono">${esc(data.voucher_number)}</span>` : ""}
</div>
<table>
  <tr><th>Monthly Salary</th><td class="num mono">${inr(data.monthly_salary)}</td></tr>
  ${data.employee.salary_structure ? `<tr><th>&nbsp;&nbsp;Basic</th><td class="num mono">${inr(data.employee.salary_structure.basic)}</td></tr>${(data.employee.salary_structure.allowances || []).map((a) => `<tr><th>&nbsp;&nbsp;+ ${esc(a.name)}</th><td class="num mono">${inr(a.amount)}</td></tr>`).join("")}${(data.employee.salary_structure.deductions || []).map((d) => `<tr><th>&nbsp;&nbsp;− ${esc(d.name)}</th><td class="num mono">${inr(d.amount)}</td></tr>`).join("")}` : ""}
  <tr><th>Total Working Days</th><td class="num mono">${data.total_working_days}</td></tr>
  <tr><th>Employee Worked Days</th><td class="num mono">${data.employee_working_days}</td></tr>
  <tr><th>Daily Salary</th><td class="num mono">${inr(data.daily_salary)}</td></tr>
  <tr><th>Earned Salary</th><td class="num mono">${inr(data.earned_salary)}</td></tr>
  <tr><th>Advance Given</th><td class="num mono">${inr(data.advance_given)}</td></tr>
  <tr class="total"><th>Final Payable</th><td class="num mono">${inr(data.final_payable)}</td></tr>
  ${data.payment_date ? `<tr><th>Payment Date</th><td class="mono">${fmtDate(data.payment_date)}</td></tr>` : ""}
  ${data.payment_mode ? `<tr><th>Payment Mode</th><td>${esc(MODE_LABELS[data.payment_mode] || data.payment_mode)}</td></tr>` : ""}
  ${data.payment_reference ? `<tr><th>Reference</th><td class="mono">${esc(data.payment_reference)}</td></tr>` : ""}
</table>
<h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:#555;margin:20px 0 6px">Advance Entries</h3>
<table><thead><tr><th>Date</th><th class="num">Amount</th><th>Notes</th></tr></thead><tbody>${adv}</tbody></table>
<div style="margin-top:28px;font-size:11px;color:#888;text-align:center;border-top:1px dashed #ccc;padding-top:12px">
Vetrich ERP · Generated ${new Date().toLocaleString("en-GB")} · Use Print → Save as PDF to download
</div>
<script>setTimeout(() => window.print(), 300);</script>
</body></html>`);
    w.document.close();
  } catch (err) { toast.error(formatApiError(err)); }
};
